<!-- FollowingForm.vue -->
<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import api from "@/services/api";
import { useDashboardStore } from "@/stores/useDashboardStore";

const dashboardStore = useDashboardStore();

const props = defineProps({
  isVisible: { type: Boolean, default: true },
});

// Estado
const isLoading = ref(false);
const error = ref(null);
const following = ref([]); // lista de quem eu estou seguindo (com flag isFollowing)
const processingId = ref(null); // id do usuário em operação (seguir/deixar de seguir)

const userStore = useUserStore();

// 🔍 termo de busca local
const searchTerm = ref("");

// se há seguindo (independente da busca)
const hasItems = computed(() => following.value && following.value.length > 0);

// lista filtrada
const filteredFollowing = computed(() => {
  const q = searchTerm.value.trim().toLowerCase();
  if (!q) return following.value;

  return following.value.filter((u) => {
    const userName = (u.userName || "").toLowerCase();
    const fullName = (u.fullName || "").toLowerCase();
    return userName.includes(q) || fullName.includes(q);
  });
});

// 🔹 Helper de avatar — agora só usa foto real; sem pravatar
function buildUserAvatar(photoPath) {
  let path = photoPath || "";

  // Se não houver caminho de foto, não retorna nada (fallback será ícone)
  if (!path) {
    return null;
  }

  // Se já for URL absoluta, devolve direto
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // Monta em cima da mesma base do backend (tirando o /api do final)
  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");

  if (!path.startsWith("/")) path = "/" + path;

  return base + path;
}

async function fetchFollowing() {
  const currentUserId = userStore.currentUser?.userId;
  if (!currentUserId) {
    error.value = "Não foi possível identificar o usuário logado.";
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    // ✅ Endpoint para QUEM EU ESTOU SEGUINDO (usuário logado)
    const res = await api.get("/Follow/following");
    const data = res.data;

    // DTO esperado: { userId, fullName/userName, photoPath }
    following.value = Array.isArray(data)
      ? data
          .filter((u) => (u.userName || "").toLowerCase() !== "sys")
          .map((u) => ({
            userId: u.userId,
            userName: u.userName || "",
            fullName: u.fullName || "",
            avatar: buildUserAvatar(u.photoPath), // se não tiver foto, fica null
            // 👇 começa como seguindo
            isFollowing: true,
          }))
      : [];
  } catch (err) {
    console.error("Erro ao carregar lista de seguindo:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Erro ao carregar quem você está seguindo.";
  } finally {
    isLoading.value = false;
  }
}

/**
 * 🔹 Toggle de seguir / seguindo
 * - Se user.isFollowing === true → DELETE /Follow/{id} (deixar de seguir → botão vira "Seguir")
 * - Se user.isFollowing === false → POST  /Follow/{id} (voltar a seguir → botão vira "Seguindo")
 */
async function handleToggleFollow(user) {
  if (!user || processingId.value) return;

  processingId.value = user.userId;
  error.value = null;

  try {
    if (user.isFollowing) {
      // 👉 Deixar de seguir (fica "Seguir")
      await api.delete(`/Follow/${user.userId}`);
      user.isFollowing = false;
    } else {
      // 👉 Voltar a seguir (fica "Seguindo")
      await api.post(`/Follow/${user.userId}`);
      user.isFollowing = true;
    }

    // 🔹 Atualiza a dashboard (contagens, etc.)
    await dashboardStore.fetchDashboard();
  } catch (err) {
    console.error("Erro ao alternar seguir/seguindo:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Não foi possível atualizar o estado de seguimento deste usuário.";
  } finally {
    processingId.value = null;
  }
}

// Carrega quando o componente for exibido
watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      fetchFollowing();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.isVisible) {
    fetchFollowing();
  }
});
</script>

<template>
  <div v-show="isVisible" class="follow-form-container">
    <h3 class="view-title text-center">Seguindo</h3>
    <p class="view-subtitle text-center">
      Pessoas que você está acompanhando no Litterae.
    </p>

    <!-- 🔍 Campo de busca — padrão idêntico ao SearchTagInput.vue -->
    <div class="search-input-wrapper" v-if="hasItems">
      <div class="icon-wrapper">
        <svg
          aria-hidden="true"
          class="search-icon"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>

      <div class="search-text-input">
        <input
          v-model="searchTerm"
          type="text"
          id="follow-search"
          placeholder="Buscar usuários que você segue..."
          class="search-input"
        />
      </div>
    </div>

    <!-- Mensagem de erro -->
    <div
      v-if="error"
      class="mt-2 mb-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
    >
      {{ error }}
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-8 text-slate-400 text-xs flex-1"
    >
      <div
        class="h-10 w-10 rounded-full border-2 border-indigo-300 border-t-transparent animate-spin mb-3"
      ></div>
      Carregando lista de quem você segue...
    </div>

    <!-- Área rolável da lista / empty state -->
    <div v-else class="list-scroll-container">
      <!-- Lista de seguindo -->
      <ul v-if="hasItems" class="space-y-3">
        <li
          v-for="user in filteredFollowing"
          :key="user.userId"
          class="rounded-xl px-3 py-2 flex items-center gap-3"
        >
          <!-- Avatar ou ícone padrão -->
          <div class="flex-shrink-0">
            <!-- Mantém avatar real quando existir -->
            <img
              v-if="user.avatar"
              :src="user.avatar"
              class="h-9 w-9 rounded-full object-cover"
              alt="Avatar"
            />
            <!-- Fallback: ícone fas fa-user no lugar do pravatar -->
            <div
              v-else
              class="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500"
            >
              <i class="fas fa-user text-sm"></i>
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <!-- USERNAME (em cima, destaque primário) -->
            <p class="text-sm font-semibold text-slate-800 truncate">
              {{ user.userName || "@" + user.userId }}
            </p>

            <!-- FULLNAME (embaixo, menor e suave) -->
            <p
              v-if="user.fullName"
              class="text-xs text-slate-500 truncate"
              :title="user.fullName"
            >
              {{ user.fullName }}
            </p>
          </div>

          <button
            type="button"
            class="follow-toggle-btn"
            :class="
 user.isFollowing
                ? 'follow-toggle-btn--following'
                : 'follow-toggle-btn--not-following'
            "
            @click="handleToggleFollow(user)"
            :disabled="processingId === user.userId"
          >
            {{ user.isFollowing ? "Seguindo" : "Seguir" }}
          </button>
        </li>
      </ul>

      <!-- Empty state – nenhum seguindo -->
      <div
        v-else
        class="flex flex-col items-center justify-center text-center py-10 text-slate-400"
      >
        <div
          class="h-20 w-20 rounded-full bg-emerald-50 flex items-center justify-center mb-4"
        >
          <i class="fas fa-user-plus fa-lg text-emerald-400"></i>
        </div>
        <p class="text-sm font-semibold text-slate-600 mb-1">
          Você ainda não está seguindo ninguém
        </p>
        <p class="text-[11px] text-slate-400 max-w-xs">
          Comece a seguir outros leitores para acompanhar as anotações e
          atividades deles por aqui.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.follow-form-container {
  padding: 0.75rem 0.5rem 0.5rem;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0; /* importante para o flex + scroll funcionar dentro do modal */
}

.view-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.view-subtitle {
  font-size: 0.85rem;
  color: var(--text-light);
  margin-top: 0.15rem;
  margin-bottom: 0.6rem;
}

.search-input-wrapper {
  margin-bottom: 1rem;
}

.search-icon {
  width: 1rem;
}

.search-text-input {
  /* container apenas para manter o mesmo markup do SearchTagInput.vue */
}

.search-input {
  height: 34px;
}

/* ============================
   ÁREA ROLÁVEL DA LISTA
============================ */
.list-scroll-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.25rem;
}

/* ============================
   BOTÃO SEGUIR / SEGUINDO
============================ */
.follow-toggle-btn {
  font-size: 11px;
  padding: 0.4rem 0.9rem;
  border-radius: 0.5rem;
  font-weight: 700;
  min-width: 110px;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.follow-toggle-btn:hover:not(:disabled) {
  /* O background-color específico fica nas variantes abaixo */
}

.follow-toggle-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.follow-toggle-btn:focus,
.follow-toggle-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

/* Estado SEGUINDO → mesmas cores dos botões ativos do componente pai */
.follow-toggle-btn--following {
  background-color: var(--brand-soft);
  color: #2f1f73;
}

.follow-toggle-btn--following:hover:not(:disabled) {
  background-color: var(--brand-soft-hover);
}

/* Estado SEGUIR → variante neutra */
.follow-toggle-btn--not-following {
  background-color: var(--surface-3);
  color: var(--text-dark);
  border: 1px solid var(--neutral-400);
}

.follow-toggle-btn--not-following:hover:not(:disabled) {
  background-color: var(--neutral-200);
}

/* Disabled */
.follow-toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.follow-toggle-btn:disabled:hover {
  cursor: not-allowed;
}
</style>